<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      loading: true,
      tableColumns: [
        { key: 'leave_type', label: 'Leave Type' },
        { key: 'branch', label: 'Branch' },
        { key: 'days_allowed', label: 'Days' },
        { key: 'year_type', label: 'Year Type' },
        { key: 'carry_forward', label: 'Carry Fwd' },
        { key: 'allow_half_day', label: 'Half Day' },
        { key: 'effective_from', label: 'Effective From' },
        { key: 'is_active', label: 'Active' },
        { key: 'actions', label: 'Actions', width: '80px' },
      ],
      policies: [],
      leaveTypeMap: {},
      branchMap: {},
      search: '',
      pagination: {
        current_page: 1,
        per_page: 50,
        last_page: 1,
        total: 0,
        links: [],
      },
    };
  },
  mounted() {
    this.fetchMeta();
    this.fetchPolicies();
  },
  methods: {
    async fetchMeta() {
     
      try {
        const [ltRes, brRes] = await Promise.allSettled([
          axios.get('/hrms/leave-types'),
          axios.get('/branches'),
        ]);

        if (ltRes.status === 'fulfilled') {
          const list = ltRes.value.data.data.data || [];
          this.leaveTypeMap = {};
          list.forEach((lt) => {
            this.leaveTypeMap[lt.id] = lt.code ? `${lt.name} (${lt.code})` : lt.name;
          });
        }
        if (brRes.status === 'fulfilled') {
          const list = brRes.value.data.data || [];
          this.branchMap = {};
          list.forEach((b) => {
            this.branchMap[b.id] = b.name || b.branch_name || b.title || `Branch #${b.id}`;
          });
        }
      } catch (error) {
        console.log(error.message);
      }
    },

    async fetchPolicies(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/leave-policies', {
          params: {
            page,
            search: this.search || undefined,
          },
        });
        this.policies = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          links: response.data.data.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.fetchPolicies(1);
    },

    leaveTypeName(row) {
      if (row.leave_type) return row.leave_type.code ? `${row.leave_type.name} (${row.leave_type.code})` : row.leave_type.name;
      return this.leaveTypeMap[row.leave_type_id] || '-';
    },

    branchName(row) {
      if (row.branch) return row.branch.name || row.branch.branch_name || '-';
      if (!row.branch_id) return 'All Branches';
      return this.branchMap[row.branch_id] || '-';
    },

    yearTypeLabel(v) {
      if (!v) return '-';
      return v.charAt(0).toUpperCase() + v.slice(1);
    },

    async toggleActive(row) {
      const previous = row.is_active;
      row.is_active = !row.is_active;
      try {
        await axios.patch(`/hrms/leave-policies/${row.id}`);
      } catch (error) {
        row.is_active = previous;
        this.$swalToast(error.message, 'error');
      }
    },

    openAdd() {
      this.$refs.policyModal.add();
      this.$openModal('hrm_leave_policy_modal');
    },

    openEdit(item) {
      this.$refs.policyModal.edit(item);
      this.$openModal('hrm_leave_policy_modal');
    },

    confirmDelete(item) {
      this.$refs.policyModal.setDelete(item);
      this.$openModal('hrm_leave_policy_delete_modal');
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-flex align-items-center justify-content-between page-breadcrumb mb-3">
        <div>
          <h4 class="page-title mb-0">Leave Policies</h4>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="openAdd">
            <i class="ti ti-circle-plus me-2"></i>Add Leave Policy
          </a>
        </div>
      </div>

      <!-- Main Card -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">

        <!-- Card Header -->
        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Leave Policy List
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>

          <!-- Search -->
          <div class="d-flex align-items-center gap-2">
            <div class="l-search-wrap">
              <input type="text" class="l-search-input" v-model="search" placeholder="Search policies..." @keyup.enter="onSearch" />
              <span style="padding:0 10px; color:#94a3b8;"><i class="ti ti-search"></i></span>
            </div>
          </div>
        </div>

        <!-- Card Body — Table -->
        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">

          <app-table
            :columns="tableColumns"
            :rows="policies"
            :pagination="pagination"
            :loading="loading"
            empty-text="No leave policies found."
            empty-icon="ti ti-file-description"
            @page-change="fetchPolicies"
          >
            <!-- Leave Type -->
            <template #leave_type="{ row }">
              <span style="font-size:12px; color:#1e293b;">{{ leaveTypeName(row) }}</span>
            </template>

            <!-- Branch -->
            <template #branch="{ row }">
              <span style="font-size:11.5px; color:#475569;">{{ branchName(row) }}</span>
            </template>

            <!-- Days -->
            <template #days_allowed="{ row }">
              <span style="font-weight:600;">{{ row.days_allowed }}</span>
            </template>

            <!-- Year Type -->
            <template #year_type="{ row }">
              <span style="font-size:11.5px; color:#475569;">{{ yearTypeLabel(row.year_type) }}</span>
            </template>

            <!-- Carry Forward -->
            <template #carry_forward="{ row }">
              <span :class="row.carry_forward ? 'lp-yes' : 'lp-no'">
                {{ row.carry_forward ? `Yes (${row.max_carry_forward_days ?? 0}d)` : 'No' }}
              </span>
            </template>

            <!-- Half Day -->
            <template #allow_half_day="{ row }">
              <span :class="row.allow_half_day ? 'lp-yes' : 'lp-no'">{{ row.allow_half_day ? 'Yes' : 'No' }}</span>
            </template>

            <!-- Effective From -->
            <template #effective_from="{ row }">
              <span style="font-size:11.5px; color:#94a3b8;">{{ row.effective_from ? $formatDate(row.effective_from) : '-' }}</span>
            </template>

            <!-- Active toggle -->
            <template #is_active="{ row }">
              <div class="form-check form-switch mb-0">
                <input class="form-check-input" type="checkbox" role="switch" :checked="row.is_active" @change="toggleActive(row)" />
              </div>
            </template>

            <!-- Actions -->
            <template #actions="{ row }">
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openEdit(row)">
                      <i class="ti ti-edit me-1"></i>Edit
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="confirmDelete(row)">
                      <i class="ti ti-trash me-1"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </app-table>

        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-leave-policy-modal ref="policyModal" @updated="fetchPolicies" />
</template>

<style scoped>
.l-search-wrap {
  display: flex;
  align-items: center;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.l-search-wrap:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, .1);
}

.l-search-input {
  border: none;
  outline: none;
  height: 34px;
  padding: 0 10px;
  font-size: 12.5px;
  color: #1e293b;
  width: 200px;
  background: transparent;
}

.lp-yes {
  font-size: 10.5px; font-weight: 600; padding: 2px 9px; border-radius: 20px;
  background: #dcfce7; color: #16a34a;
}
.lp-no {
  font-size: 10.5px; font-weight: 600; padding: 2px 9px; border-radius: 20px;
  background: #f1f5f9; color: #64748b;
}
</style>